import Web3 from 'web3';
import { BigNumber } from 'bignumber.js';
import { getToken } from 'blockchain/tokensMetadata';

import dsProxyRegistryAbi from 'blockchain/abi/ds-proxy-registry.json';
import erc20Abi from 'blockchain/abi/erc20.json';
import { zero } from 'constants/number';
import { MULTICHAIN_SETUP } from 'constants/variables';
import { amountFromWei, amountToWei } from './common-function';
import { OpenLoanState } from 'app/pages/NewLoan/hook/balance.hook';
import { Api } from 'utils/services/Api';
import { getRecoilExternalLoadable } from 'app/components/RecoilExternalState';
import { web3Service } from 'store/web3';

const { PROXY_REGISTRY } = MULTICHAIN_SETUP.polka.address;

export const initialWeb3 = (rpc?: string) => {
  if (rpc) {
    const provider = new Web3.providers.HttpProvider(rpc);
    return new Web3(provider);
  }
  return new Web3(Web3.givenProvider);
};

export const initialContract = (abi: any, address: string, rpc?: string) => {
  const service = getRecoilExternalLoadable(web3Service)?.contents as Api;
  //console.log(service);
  if (!rpc && service) {
    return service.initContract(abi, address);
  }
  const web3 = initialWeb3(rpc);
  return new web3.eth.Contract(abi, address);
};

export const checkApprove = async (
  abi: any,
  contract: string,
  owner: string,
  spender: string,
): Promise<boolean> => {
  try {
    const checkContract = initialContract(abi, contract);
    const approve = await checkContract.methods
      .allowance(owner, spender)
      .call();

    if (!approve) {
      return false;
    }
    return approve > 0;
  } catch (err) {
    //console.log(err);
    return false;
  }
};

export const getProxyAddress = async (address): Promise<string> => {
  const web3 = new Web3(Web3.givenProvider);
  const contract = new web3.eth.Contract(
    dsProxyRegistryAbi as any,
    PROXY_REGISTRY,
  );

  return await contract.methods.proxies(address).call();
};

export const getTotalBalanceByTokenAddress = async (
  address,
  tokenAddress,
  token?: string,
): Promise<{ balance: string; token: string }> => {
  const web3 = new Web3(Web3.givenProvider);
  const contract = new web3.eth.Contract(erc20Abi as any, tokenAddress);

  const balance = await contract.methods.balanceOf(address).call();
  const symbol = await contract.methods.symbol().call();
  if (token) {
    const t = getToken(token);
    //console.log('t', t);
    if (t.precision === 18) {
      return { balance: Web3.utils.fromWei(balance, 'ether'), token: symbol };
    }
    const cvBalance = new BigNumber(balance);
    return {
      balance: amountFromWei(cvBalance, t.precision).toString(),
      token: symbol,
    };
  }
  return { balance: Web3.utils.fromWei(balance, 'ether'), token: symbol };
};

export const getETHBalance = async (address): Promise<string> => {
  const web3 = new Web3(Web3.givenProvider);
  const balance = await web3.eth.getBalance(address);
  return web3.utils.fromWei(balance, 'ether');
};

export interface OpenCallData {
  dssProxyActions: any;
  dssCdpManager: string;
  mcdJug: string;
  mcdJoinDai: string;
  joinsIlk: string;
}

export interface Amount {
  ilk: string;
  token: string;
  depositAmount: BigNumber;
  borrowAmount: BigNumber;
  proxyAddress: string;
}

export interface LockAmount extends OpenLoanState {
  id: BigNumber;
}

export const getOpenCallData = (data: OpenLoanState, context: OpenCallData) => {
  const { dssProxyActions, dssCdpManager, mcdJug, mcdJoinDai, joinsIlk } =
    context;
  const { ilk, token, depositAmount, borrowAmount, proxyAddress, service } =
    data;

  if (depositAmount.gt(zero) && borrowAmount.gt(zero)) {
    if (token === 'HLB') {
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.openLockETHAndDraw(
          dssCdpManager,
          mcdJug,
          joinsIlk,
          mcdJoinDai,
          Web3.utils.utf8ToHex(ilk),
          amountToWei(borrowAmount, 'DAI'),
        );
    }

    return service
      .initContract(dssProxyActions, proxyAddress)
      .methods.openLockGemAndDraw(
        dssCdpManager,
        mcdJug,
        joinsIlk,
        mcdJoinDai,
        Web3.utils.utf8ToHex(ilk),
        amountToWei(depositAmount, token),
        amountToWei(borrowAmount, 'DAI'),
        true,
      );
  }

  if (depositAmount.gt(zero) && borrowAmount.isZero()) {
    //console.log('only deposit');
    if (token === 'HLB') {
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.openLockETHAndDraw(
          dssCdpManager,
          mcdJug,
          joinsIlk,
          mcdJoinDai,
          Web3.utils.utf8ToHex(ilk),
          zero.toFixed(0),
        );
    }
    return service
      .initContract(dssProxyActions, proxyAddress)
      .methods.openLockGemAndDraw(
        dssCdpManager,
        mcdJug,
        joinsIlk,
        mcdJoinDai,
        Web3.utils.utf8ToHex(ilk),
        amountToWei(depositAmount, token),
        zero.toFixed(0),
        true,
      );
  }

  return service
    .initContract(dssProxyActions, proxyAddress)
    .methods.open(dssCdpManager, Web3.utils.utf8ToHex(ilk), proxyAddress);
};

export const getDepositAndGenerateCallData = (
  data: LockAmount,
  context: OpenCallData,
) => {
  const { dssProxyActions, dssCdpManager, mcdJoinDai, mcdJug, joinsIlk } =
    context;
  const { id, token, depositAmount, borrowAmount, ilk, proxyAddress, service } =
    data;

  if (depositAmount.gt(zero) && borrowAmount.gt(zero)) {
    //console.log('both deposit and borrow');
    //console.log(Web3.utils.utf8ToHex(ilk));
    if (token === 'HLB') {
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.lockETHAndDraw(
          dssCdpManager,
          mcdJug,
          joinsIlk,
          mcdJoinDai,
          id.toString(),
          amountToWei(borrowAmount, 'DAI').toFixed(0),
        );
    }
    return service
      .initContract(dssProxyActions, proxyAddress)
      .methods.lockGemAndDraw(
        dssCdpManager,
        mcdJug,
        joinsIlk,
        mcdJoinDai,
        id.toString(),
        amountToWei(depositAmount, token),
        amountToWei(borrowAmount, 'DAI'),
        true,
      );
  }

  if (depositAmount.gt(zero)) {
    //console.log('only deposit');
    if (token === 'HLB') {
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.lockETH(dssCdpManager, joinsIlk, id.toString());
    }
    return service
      .initContract(dssProxyActions, proxyAddress)
      .methods.lockGem(
        dssCdpManager,
        joinsIlk,
        id.toString(),
        amountToWei(depositAmount, token),
        true,
      );
  }

  return service
    .initContract(dssProxyActions, proxyAddress)
    .methods.draw(
      dssCdpManager,
      mcdJug,
      mcdJoinDai,
      id.toString(),
      amountToWei(borrowAmount, 'DAI').toFixed(0),
    );
};

export interface WithdrawAndPaybackData {
  id: BigNumber;
  token: string;
  ilk: string;
  withdrawAmount: BigNumber;
  paybackAmount: BigNumber;
  proxyAddress: string;
  shouldPaybackAll: boolean;
  service: Api;
}

export const getWithdrawAndPaybackCallData = (
  data: WithdrawAndPaybackData,
  context: OpenCallData,
) => {
  const { dssProxyActions, dssCdpManager, mcdJoinDai, joinsIlk } = context;
  const {
    id,
    token,
    withdrawAmount,
    paybackAmount,
    shouldPaybackAll,
    proxyAddress,
    service,
  } = data;

  if (withdrawAmount.gt(zero) && paybackAmount.gt(zero)) {
    //console.log('both deposit and borrow');
    //console.log(withdrawAmount);
    if (token === 'HLB') {
      if (shouldPaybackAll) {
        return service
          .initContract(dssProxyActions, proxyAddress)
          .methods.wipeAllAndFreeETH(
            dssCdpManager,
            joinsIlk,
            mcdJoinDai,
            id.toString(),
            amountToWei(withdrawAmount, token).toFixed(0),
          );
      }
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.wipeAndFreeETH(
          dssCdpManager,
          joinsIlk,
          mcdJoinDai,
          id.toString(),
          amountToWei(withdrawAmount, token).toFixed(0),
          amountToWei(paybackAmount, 'DAI').toFixed(0),
        );
    }
    if (shouldPaybackAll) {
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.wipeAllAndFreeGem(
          dssCdpManager,
          joinsIlk,
          mcdJoinDai,
          id.toString(),
          amountToWei(withdrawAmount, token),
        );
    }
    return service
      .initContract(dssProxyActions, proxyAddress)
      .methods.wipeAndFreeGem(
        dssCdpManager,
        joinsIlk,
        mcdJoinDai,
        id.toString(),
        amountToWei(withdrawAmount, token),
        amountToWei(paybackAmount, 'DAI'),
      );
  }

  if (withdrawAmount.gt(zero)) {
    //console.log('only deposit');
    if (token === 'HLB') {
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.freeETH(
          dssCdpManager,
          joinsIlk,
          id.toString(),
          amountToWei(withdrawAmount, token).toFixed(0),
        );
    }
    return service
      .initContract(dssProxyActions, proxyAddress)
      .methods.freeGem(
        dssCdpManager,
        joinsIlk,
        id.toString(),
        amountToWei(withdrawAmount, token),
      );
  }

  if (paybackAmount.gt(zero)) {
    //console.log('only payback');
    if (shouldPaybackAll) {
      //console.log('12312312312');
      return service
        .initContract(dssProxyActions, proxyAddress)
        .methods.wipeAll(dssCdpManager, mcdJoinDai, id.toString());
    }
    return service
      .initContract(dssProxyActions, proxyAddress)
      .methods.wipe(
        dssCdpManager,
        mcdJoinDai,
        id.toString(),
        amountToWei(paybackAmount, 'DAI').toFixed(0),
      );
  }

  return false;
};
